import { useSyncExternalStore } from 'react'

import styles from '@/pages/app/Settings/styles.module.scss'

import { Switch } from '@/components/Switch'
import Tooltip from '@/components/Tooltip'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { isGranularDataControlsUiVisible } from '@/pages/app/Settings/dataControls'
import FeatureEnterpriseTag from '@/pages/app/Settings/FeatureEnterpriseTag'
import { Enterprise, EnterpriseSettings, PlanType, SettingType } from '@/types'

const MODEL_TRAINING_DESC =
  'Controls whether Wispr can use inputs, outputs, and other data from your organization members to train our models.'

interface DataControlsSectionProps {
  enterprise: Enterprise | null
  planType: PlanType | null
  isAdmin: boolean
  optimisticSettings: EnterpriseSettings
  applySettingsPatch: (patch: Partial<EnterpriseSettings>) => Promise<void>
}

const DataControlsSection = ({
  enterprise,
  planType,
  isAdmin,
  optimisticSettings,
  applySettingsPatch,
}: DataControlsSectionProps) => {
  const granularUiVisible = isGranularDataControlsUiVisible(optimisticSettings)
  const legacyZdr = optimisticSettings[SettingType.zdr] || false

  const enterprisePlanActive = planType === PlanType.Enterprise
  // View as Admin hides upgrade-only rows instead of showing them locked.
  const hideUpsell = useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null && !enterprisePlanActive
  const zdrLocked = enterprise?.settings?.zdrLocked ?? false
  const hipaaBaaSigned = enterprise?.hipaaBaaSigned ?? false

  const hipaaPrivacyLockedTooltip = 'Your HIPAA agreement does not allow data to be used for model training.'

  const legacyPrivacyTooltip = !enterprisePlanActive
    ? 'Upgrade to Enterprise plan to modify this'
    : zdrLocked
      ? 'Data sharing has been locked by Wispr. Contact support to modify.'
      : hipaaBaaSigned
        ? hipaaPrivacyLockedTooltip
        : !isAdmin
          ? 'Only admins can modify this'
          : undefined

  const legacyPrivacyToggleDisabled = !enterprisePlanActive || !isAdmin || zdrLocked || (hipaaBaaSigned && legacyZdr)

  return (
    <>
      <div className={styles.settingInfo}>
        <div className={styles.settingTitle}>Universal controls</div>
        <div className={styles.settingDescriptionRow}>
          <span>Enterprise-grade security, at your fingertips</span>
        </div>
      </div>

      {hideUpsell ? null : granularUiVisible ? (
        <div className={`${styles.settingsItem} ${styles.alignTop}`} data-testid="model-training-setting">
          {enterprisePlanActive ? (
            <>
              <div className={styles.labelBlock}>
                <div className={styles.toggleLabel}>Model training</div>
                <div className={styles.labelDescription}>{MODEL_TRAINING_DESC}</div>
              </div>
              <div className={styles.staticValue}>Off for everyone</div>
            </>
          ) : (
            <>
              <div className={styles.labelBlock}>
                <div className={styles.toggleLabel}>Model training</div>
                <div className={styles.labelDescription}>
                  {MODEL_TRAINING_DESC} Managed individually means that each member can decide.
                </div>
                <div className={styles.upgradeCta}>
                  Force disable for all members by upgrading to <FeatureEnterpriseTag />
                </div>
              </div>
              <div className={styles.staticValue}>Managed individually</div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.settingsItem}>
          <div className={styles.toggleLabel}>Enforce zero data retention</div>
          <Tooltip text={legacyPrivacyTooltip} placement="top" hideArrow>
            <Switch
              checked={legacyZdr}
              onInputChange={(checked) => applySettingsPatch({ zdr: checked })}
              disabled={legacyPrivacyToggleDisabled}
            />
          </Tooltip>
          {!enterprisePlanActive && <FeatureEnterpriseTag />}
        </div>
      )}
    </>
  )
}

export default DataControlsSection
